'use client';

import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
} from 'react';
import styles from './button.module.css';
import { Spinner } from './spinner';

/**
 * Button - the design-system button. Four visual weights and two sizes, token-only,
 * square-ish per the design system. One canonical control so every action across the
 * app reads from the same vocabulary:
 *   - `solid`    the one filled call-to-action of a surface (accent fill, like the
 *                dock's page action); at most one per view - e.g. the submit in a
 *                mobile sheet, where the soft accent would read as a second input box.
 *   - `accent`   the highlighted / primary action (filled-soft accent).
 *   - `ghost`    a bordered, transparent secondary action.
 *   - `minimal`  a quiet, borderless action (text that lights up on hover).
 * `danger` recolours any variant for destructive actions (remove, decline). Renders a
 * real `<button>` (defaults to `type="button"`), forwards its ref, and passes through
 * every native button prop.
 */

export type ButtonVariant = 'solid' | 'accent' | 'ghost' | 'minimal';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Recolour to the destructive tone (works with any variant). */
  danger?: boolean;
  /**
   * Hold-to-confirm (docs/MOBILE.md §4): on touch/pen, `onClick` fires only
   * after a ~550ms press with a visible fill - a plain tap does nothing, so an
   * act that cannot be undone cannot happen by accident. Mouse clicks and
   * keyboard activation stay immediate: the accidental-activation risk this
   * guards against is a stray thumb, not a deliberate pointer. No consumer
   * since 2026-09-28 (the feed's "Mark all read" became one tap with an undo),
   * so the gauge docs/MOBILE.md §7 rules (2026-09-28) is built with the next
   * consumer: a hold telegraphs itself by motion, never by words. The gauge
   * starts at touch-down, a short tap springs it back from a visible minimum,
   * and no caption, title or toast explains it.
   */
  holdToConfirm?: boolean;
  /**
   * Busy state: the action is in flight (a pending `useTransition`). Forces
   * `disabled` (so the in-flight action cannot be double-fired), sets
   * `aria-busy`, and shows a leading Spinner while keeping the label - the one
   * canonical "working" affordance, replacing the ~155 hand-wired
   * `disabled={isPending}` bindings and the ad-hoc `pending ? '…ing' : '…'`
   * label swaps. Combine with `disabled` for validity gates: the button is
   * disabled when EITHER is true (`disabled={!valid} pending={isPending}`).
   */
  pending?: boolean;
}

/** Must match the ::before transition in button.module.css. */
const HOLD_MS = 550;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'ghost',
    size = 'md',
    danger,
    holdToConfirm,
    pending,
    type = 'button',
    className,
    onClick,
    disabled,
    children,
    ...rest
  },
  ref,
) {
  const [holding, setHolding] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pointerTypeRef = useRef('mouse');

  const cancelHold = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setHolding(false);
  }, []);
  useEffect(() => cancelHold, [cancelHold]);

  const holdProps = holdToConfirm
    ? {
        onPointerDown: (e: React.PointerEvent<HTMLButtonElement>) => {
          pointerTypeRef.current = e.pointerType;
          if (e.pointerType === 'mouse') return;
          setHolding(true);
          timerRef.current = setTimeout(() => {
            timerRef.current = null;
            setHolding(false);
            // The held press IS the activation; the consumer's handler takes a
            // MouseEvent it never inspects (every call site passes a closure).
            onClick?.(e as unknown as React.MouseEvent<HTMLButtonElement>);
          }, HOLD_MS);
        },
        onPointerUp: cancelHold,
        onPointerLeave: cancelHold,
        onPointerCancel: cancelHold,
        // A long press must not open the platform context menu mid-hold.
        onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
        onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
          // Keyboard activation (detail 0) and mouse clicks fire immediately;
          // the synthetic click after a touch tap/hold is swallowed (the hold
          // timer already fired the action, or the tap was too short - by
          // design nothing happens then).
          if (e.detail === 0 || pointerTypeRef.current === 'mouse') onClick?.(e);
        },
      }
    : { onClick };

  return (
    <button
      ref={ref}
      type={type}
      className={className ? `${styles.btn} ${className}` : styles.btn}
      data-variant={variant}
      data-size={size}
      data-danger={danger || undefined}
      data-hold={holdToConfirm || undefined}
      data-holding={holding || undefined}
      data-pending={pending || undefined}
      disabled={disabled || pending || undefined}
      aria-busy={pending || undefined}
      {...rest}
      {...holdProps}
    >
      {pending && <Spinner />}
      {children}
    </button>
  );
});
